import { ThemeConfig, theme as antdTheme } from "antd";

// Single source of theme colour values for the app. Splitting light/dark
// palettes here keeps LocaleContext free of hex literals and means a
// designer adjustment only touches one file.

const brand = {
  primary: "#0077ff",
  // Sider stays brand-navy in both modes, but tinted lighter in dark so it
  // sits ABOVE the dark page (#000) as a real panel — going darker than
  // the page blends it back in.
  siderLight: "#273456",
  siderDark: "#1d2747",
  // Expanded sub-menu drops one step from the sider so hierarchy still
  // reads when nested items are open.
  subMenuLight: "#1c263f",
  subMenuDark: "#141a30",
} as const;

const light = {
  // Body bg shown behind the antd layout (visible on overscroll / before
  // first paint). Set imperatively because antd v6 cssVar mode scopes
  // `--ant-color-bg-layout` to its `.ant-app` subtree.
  pageBackground: "#f5f5f5",
} as const;

const dark = {
  pageBackground: "#000000",
  // antd's darkAlgorithm derives a darker variant of the brand (#0077ff →
  // #0368dc) which drops below WCAG AA on the dark surface. Pin link
  // colours to a brighter blue in dark mode so anchor text (worker names,
  // breadcrumbs, etc.) stays legible.
  colorLink: "#4d9eff",
  colorLinkHover: "#6cb3ff",
  colorLinkActive: "#3388ee",
} as const;

export function pageBackgroundColor(isDark: boolean) {
  return isDark ? dark.pageBackground : light.pageBackground;
}

export function getThemeConfig(isDark: boolean): ThemeConfig {
  return {
    // Emit CSS variables (`--ant-color-primary`, `--ant-font-family`, …) at
    // runtime so non-antd surfaces (CSS Modules, app.less, inline styles)
    // can consume the live theme via `var(--ant-color-X)`. v6 enables this
    // by default — we set it explicitly so a future v7 default change can't
    // quietly break custom-CSS color references, and so dark-mode (algorithm
    // switching) propagates to those surfaces without a rebuild.
    cssVar: { prefix: "ant" },
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily:
        "Lato, 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
      fontSize: 14,
      colorPrimary: brand.primary,
      colorLink: isDark ? dark.colorLink : brand.primary,
      colorSuccess: "#52c41a",
      colorWarning: "#faad14",
      colorError: "#f5222d",
      ...(isDark && {
        colorLinkHover: dark.colorLinkHover,
        colorLinkActive: dark.colorLinkActive,
      }),
      // Text colors are intentionally NOT overridden so antd's algorithm
      // (defaultAlgorithm vs darkAlgorithm) can derive them. Pinning v4-style
      // `rgba(0,0,0,X)` here would force light-mode values even when dark
      // mode is active.
      fontSizeHeading3: 20,
      fontSizeHeading4: 16,
    },
    components: {
      Layout: {
        // antd v6's default headerBg is the legacy dark navy (#001529),
        // not colorBgContainer. Pin it to the theme container CSS variable
        // so it resolves to white in light mode and the dark surface in
        // dark mode.
        headerBg: "var(--ant-color-bg-container)",
        headerHeight: 50,
        headerPadding: "0 24px",
        siderBg: isDark ? brand.siderDark : brand.siderLight,
      },
      Menu: {
        darkItemBg: isDark ? brand.siderDark : brand.siderLight,
        darkSubMenuItemBg: isDark ? brand.subMenuDark : brand.subMenuLight,
      },
      Table: { fontFamily: "Lato" },
      Breadcrumb: { fontSize: 16 },
    },
  };
}
